import { useEffect, useState } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material'
import { useMutation, useQuery } from '@redwoodjs/web'
import { toast } from '@redwoodjs/web/toast'

import RichTextEditor from 'src/components/Message/RichTextEditor/RichTextEditor'

const ADMIN_INFO_QUERY = gql`
  query AdminInfoQuery {
    adminInfo {
      id
      content
      updatedAt
    }
  }
`

const UPDATE_ADMIN_INFO = gql`
  mutation UpdateAdminInfoMutation($input: UpdateAdminInfoInput!) {
    updateAdminInfo(input: $input) {
      id
      content
      updatedAt
    }
  }
`

const stripHtml = (html: string) =>
  html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim()

const contentSx = {
  fontSize: '14px',
  lineHeight: 1.6,
  color: 'text.primary',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  '& ul, & ol': { pl: 3, my: 0.5 },
  '& p': { my: 0.5 },
} as const

type Props = {
  open: boolean
  onClose: () => void
}

const AdminInfoModal = ({ open, onClose }: Props) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')

  const { data, loading } = useQuery(ADMIN_INFO_QUERY, {
    fetchPolicy: 'cache-and-network',
  })
  const savedContent = data?.adminInfo?.content ?? ''

  const [saveInfo, { loading: saving }] = useMutation(UPDATE_ADMIN_INFO, {
    refetchQueries: ['AdminInfoQuery'],
    awaitRefetchQueries: true,
    onCompleted: (result) => {
      setDraft(result.updateAdminInfo.content)
      setEditing(false)
      toast.success('Informationen sparades')
    },
    onError: (error) => toast.error(error.message),
  })

  useEffect(() => {
    if (!open) {
      setEditing(false)
      return
    }
    setDraft(savedContent)
  }, [open, savedContent])

  const handleClose = () => {
    if (saving) return
    setEditing(false)
    onClose()
  }

  const handleSave = () => {
    saveInfo({ variables: { input: { content: draft } } })
  }

  const hasContent = Boolean(stripHtml(savedContent))

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={saving}
      sx={{ zIndex: 1400 }}
      aria-labelledby="admin-info-modal-title"
    >
      <DialogTitle
        id="admin-info-modal-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
        }}
      >
        <Typography component="span" variant="h6" fontWeight={500}>
          Info
        </Typography>
        <IconButton
          aria-label="Stäng"
          onClick={handleClose}
          disabled={saving}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading && !data ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : editing ? (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 1,
              p: 1.5,
            }}
          >
            <RichTextEditor
              value={draft}
              onChange={setDraft}
              placeholder="Skriv information..."
              minHeight={180}
              editorKey="admin-info"
              showToolbar
            />
          </Box>
        ) : hasContent ? (
          <Box
            sx={contentSx}
            dangerouslySetInnerHTML={{ __html: savedContent }}
          />
        ) : (
          <Typography color="text.secondary">
            Ingen information har lagts till ännu.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {editing ? (
          <>
            <Button
              onClick={() => {
                setDraft(savedContent)
                setEditing(false)
              }}
              disabled={saving}
            >
              Avbryt
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={{ color: 'common.white', minWidth: 120 }}
            >
              {saving ? 'Sparar...' : 'Spara'}
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            onClick={() => {
              setDraft(savedContent)
              setEditing(true)
            }}
            disabled={loading && !data}
            sx={{ color: 'common.white' }}
          >
            Ändra
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default AdminInfoModal
